/**
 * Control Portal - Settings Page
 * Platform-wide settings management
 */
import { useState, useEffect } from 'react';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Settings, Shield, Bell, Mail, Globe, Database, 
  RefreshCw, Save, Key, Clock, Loader2
} from 'lucide-react';

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    platformName: 'TrackOtic',
    supportEmail: 'support@sentinelx.com',
    alertEmailEnabled: true,
    alertSmsEnabled: false,
    tempBreachThreshold: 2,
    offlineThresholdMinutes: 30,
    sessionTimeoutMinutes: 60,
    maxLoginAttempts: 5,
    enableAuditLog: true,
    enableWebhooks: true,
    dataRetentionDays: 365,
    telemetryIntervalSeconds: 60
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await controlApi.getSettings();
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await controlApi.updateSettings(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Settings className="w-8 h-8 text-blue-500" />
            Platform Settings
          </h1>
          <p className="text-slate-400 mt-1">Configure global platform settings</p>
        </div>
        <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-400" />
            General Settings
          </h2>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Platform Name</Label>
              <Input 
                value={settings.platformName}
                onChange={(e) => setSettings({...settings, platformName: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Support Email</Label>
              <Input 
                value={settings.supportEmail}
                onChange={(e) => setSettings({...settings, supportEmail: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
          </div>
        </div>

        {/* Alert Settings */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-400" />
            Alert Configuration
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white">Email Alerts</p>
                <p className="text-slate-500 text-sm">Send alerts via email</p>
              </div>
              <Switch 
                checked={settings.alertEmailEnabled}
                onCheckedChange={(v) => setSettings({...settings, alertEmailEnabled: v})}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white">SMS Alerts</p>
                <p className="text-slate-500 text-sm">Send alerts via SMS</p>
              </div>
              <Switch 
                checked={settings.alertSmsEnabled}
                onCheckedChange={(v) => setSettings({...settings, alertSmsEnabled: v})}
              />
            </div>
            <div>
              <Label className="text-slate-300">Temp Breach Threshold (°C deviation)</Label>
              <Input 
                type="number"
                value={settings.tempBreachThreshold}
                onChange={(e) => setSettings({...settings, tempBreachThreshold: parseInt(e.target.value)})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Offline Threshold (minutes)</Label>
              <Input 
                type="number"
                value={settings.offlineThresholdMinutes}
                onChange={(e) => setSettings({...settings, offlineThresholdMinutes: parseInt(e.target.value)})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-400" />
            Security Settings
          </h2>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Session Timeout (minutes)</Label>
              <Input 
                type="number"
                value={settings.sessionTimeoutMinutes}
                onChange={(e) => setSettings({...settings, sessionTimeoutMinutes: parseInt(e.target.value)})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Max Login Attempts</Label>
              <Input 
                type="number"
                value={settings.maxLoginAttempts}
                onChange={(e) => setSettings({...settings, maxLoginAttempts: parseInt(e.target.value)})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white">Audit Logging</p>
                <p className="text-slate-500 text-sm">Log all user actions</p>
              </div>
              <Switch 
                checked={settings.enableAuditLog}
                onCheckedChange={(v) => setSettings({...settings, enableAuditLog: v})}
              />
            </div>
          </div>
        </div>

        {/* Integration Settings */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Key className="w-5 h-5 text-purple-400" />
            Integration Settings
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white">Webhooks</p>
                <p className="text-slate-500 text-sm">Enable webhook notifications</p>
              </div>
              <Switch 
                checked={settings.enableWebhooks}
                onCheckedChange={(v) => setSettings({...settings, enableWebhooks: v})}
              />
            </div>
            <div>
              <Label className="text-slate-300">Telemetry Interval (seconds)</Label>
              <Input 
                type="number"
                value={settings.telemetryIntervalSeconds}
                onChange={(e) => setSettings({...settings, telemetryIntervalSeconds: parseInt(e.target.value)})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Data Retention (days)</Label>
              <Input 
                type="number"
                value={settings.dataRetentionDays}
                onChange={(e) => setSettings({...settings, dataRetentionDays: parseInt(e.target.value)})}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
